// Report (obj_type "R") model — the twasta-side JSON persisted at
// Metadata_Model/report_models/model_<obj>.json. The JRXML itself lives in
// sidecar files (Report_Design/<obj>/*.jrxml) transported alongside the
// model by load/save-report-object.
//
// CRITERIA reuses the VISUAL rule shape verbatim (CriteriaRule) — plus the
// report-specific JASPER_PARAM binding (the case-sensitive $P{} name).

import type { CriteriaRule } from '../visual/types'

export type ReportOutputFormat =
  | 'html' | 'pdf' | 'excel' | 'xlsx' | 'csv' | 'docx' | 'pptx' | 'rtf'

export const REPORT_FORMAT_OPTIONS: ReportOutputFormat[] = [
  'html', 'pdf', 'excel', 'csv', 'docx', 'pptx', 'rtf',
]

export interface ReportDeclaredParam {
  name: string
  java_class: string
}

export interface ReportModel {
  OBJ_NAME: string
  OBJ_TYPE: 'R'
  TITLE?: string
  DESCRIPTION?: string
  REPORT: {
    MAIN_JRXML: string
    JRXML_FILES: string[]
    DEFAULT_OUTPUT_FORMAT: ReportOutputFormat
    OUTPUT_FORMATS: ReportOutputFormat[]
    /** Save-time cache of the JRXML's declared $P{} params. */
    PARAMETERS: ReportDeclaredParam[]
    /**
     * Reverse-only provenance of an IMPORTED report: the app-server filenames
     * (`design_source`, `xsd_source`), the legacy datasource config, and the
     * enterprise folder each file was read from. Never authored here — the
     * designer carries it through untouched, because deploy routes the report
     * by it.
     */
    LEGACY_SOURCE?: Record<string, unknown>
    [key: string]: unknown
  }
  SQLMODEL: {
    DATABASE_DETAIL: { CONNECTION_ID: string; [key: string]: unknown }
    CRITERIA: {
      query: { rules: CriteriaRule[]; condition: 'and' | 'or'; [key: string]: unknown }
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

export function defaultReportJrxml(objName: string): string {
  const name = (objName || 'Report').replace(/[^A-Za-z0-9_]/g, '_')
  return `<?xml version="1.0" encoding="UTF-8"?>
<jasperReport xmlns="http://jasperreports.sourceforge.net/jasperreports"
              xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
              xsi:schemaLocation="http://jasperreports.sourceforge.net/jasperreports
              http://jasperreports.sourceforge.net/xsd/jasperreport.xsd"
              name="${name}"
              pageWidth="595"
              pageHeight="842"
              columnWidth="555"
              leftMargin="20"
              rightMargin="20"
              topMargin="20"
              bottomMargin="20">

    <queryString>
        <![CDATA[]]>
    </queryString>

    <title>
        <band height="50">
            <staticText>
                <reportElement x="0" y="0" width="555" height="40"/>
                <textElement textAlignment="Center" verticalAlignment="Middle">
                    <font size="20" isBold="true"/>
                </textElement>
                <text><![CDATA[${name}]]></text>
            </staticText>
        </band>
    </title>

    <detail>
        <band height="20"/>
    </detail>

</jasperReport>
`
}

export function blankReportModel(objName: string): ReportModel {
  return {
    OBJ_NAME: objName,
    OBJ_TYPE: 'R',
    TITLE: objName,
    REPORT: {
      MAIN_JRXML: 'report.jrxml',
      JRXML_FILES: ['report.jrxml'],
      DEFAULT_OUTPUT_FORMAT: 'pdf',
      OUTPUT_FORMATS: ['html', 'pdf', 'excel', 'csv', 'docx'],
      PARAMETERS: [],
    },
    SQLMODEL: {
      DATABASE_DETAIL: { CONNECTION_ID: '' },
      CRITERIA: { query: { rules: [], condition: 'and' } },
    },
  }
}

/** Normalize whatever load-report-object returned into a full ReportModel. */
export function sanitizeReportModel(raw: unknown, objName: string): ReportModel {
  const base = blankReportModel(objName)
  if (!raw || typeof raw !== 'object') return base
  const m = raw as Record<string, unknown>
  const report = (m.REPORT && typeof m.REPORT === 'object' ? m.REPORT : {}) as Record<string, unknown>
  const sqlmodel = (m.SQLMODEL && typeof m.SQLMODEL === 'object' ? m.SQLMODEL : {}) as Record<string, unknown>
  const criteria = (sqlmodel.CRITERIA && typeof sqlmodel.CRITERIA === 'object'
    ? sqlmodel.CRITERIA : {}) as Record<string, unknown>
  const query = (criteria.query && typeof criteria.query === 'object'
    ? criteria.query : {}) as Record<string, unknown>
  // Every spread keeps the keys this designer knows nothing about. The model
  // is not only the designer's: an imported report carries REPORT.LEGACY_SOURCE
  // — the enterprise folder it deploys into, the filenames it deploys under,
  // the XSD deploy edits rather than regenerates — and rebuilding the block
  // from the known fields alone dropped all of it on the first save, sending
  // the report to the common folder on the next deploy.
  return {
    ...m,
    OBJ_NAME: String(m.OBJ_NAME || objName),
    OBJ_TYPE: 'R',
    TITLE: String(m.TITLE || objName),
    REPORT: {
      ...report,
      MAIN_JRXML: String(report.MAIN_JRXML || base.REPORT.MAIN_JRXML),
      JRXML_FILES: Array.isArray(report.JRXML_FILES) && report.JRXML_FILES.length
        ? (report.JRXML_FILES as string[])
        : base.REPORT.JRXML_FILES,
      DEFAULT_OUTPUT_FORMAT:
        (report.DEFAULT_OUTPUT_FORMAT as ReportOutputFormat) || base.REPORT.DEFAULT_OUTPUT_FORMAT,
      OUTPUT_FORMATS: Array.isArray(report.OUTPUT_FORMATS) && report.OUTPUT_FORMATS.length
        ? (report.OUTPUT_FORMATS as ReportOutputFormat[])
        : base.REPORT.OUTPUT_FORMATS,
      PARAMETERS: Array.isArray(report.PARAMETERS)
        ? (report.PARAMETERS as ReportDeclaredParam[])
        : [],
    },
    SQLMODEL: {
      ...sqlmodel,
      DATABASE_DETAIL: {
        ...((sqlmodel.DATABASE_DETAIL as Record<string, unknown>) || {}),
        CONNECTION_ID: String(
          ((sqlmodel.DATABASE_DETAIL as Record<string, unknown>)?.CONNECTION_ID) || '',
        ),
      },
      CRITERIA: {
        ...criteria,
        query: {
          ...query,
          rules: Array.isArray(query.rules) ? (query.rules as CriteriaRule[]) : [],
          condition: query.condition === 'or' ? 'or' : 'and',
        },
      },
    },
  }
}
